/**
 * AnalyseIAPage — Génération d'analyse astrologique via Claude API
 *
 * Streaming SSE pour affichage progressif, sauvegarde en BDD,
 * export Word (.docx).
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { colors } from '../styles/tokens.js';
import { useClient } from '../contexts/ClientContext.js';
import { downloadAnalysePdf } from '../services/pdfGenerator.js';
import { ShareAnalyseModal } from '../components/ShareAnalyseModal.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface SavedAnalyse {
  id: number;
  modules: string;
  contenu: string;
  modelVersion: string;
  createdAt: string;
  chartId?: number;
  chart?: {
    id: number;
    type: 'NATAL' | 'REVOLUTION_SOLAIRE' | 'TRANSITS' | 'PROGRESSIONS';
    anneeRs?: number | null;
  };
}

/** Libellé court pour le chart d'origine d'une analyse */
function chartLabel(chart?: SavedAnalyse['chart']): string {
  if (!chart) return 'Thème';
  switch (chart.type) {
    case 'NATAL': return 'Thème natal';
    case 'REVOLUTION_SOLAIRE': return chart.anneeRs ? `RS ${chart.anneeRs}` : 'Révolution solaire';
    case 'TRANSITS': return 'Transits';
    case 'PROGRESSIONS': return 'Progressions';
    default: return 'Thème';
  }
}

function chartBadgeColors(type?: SavedAnalyse['chart'] extends infer T ? T extends { type: infer U } ? U : never : never): { bg: string; color: string } {
  switch (type) {
    case 'NATAL': return { bg: '#f5f3ff', color: '#6d28d9' };
    case 'REVOLUTION_SOLAIRE': return { bg: '#fff7ed', color: '#c2410c' };
    case 'TRANSITS': return { bg: '#ecfeff', color: '#0891b2' };
    case 'PROGRESSIONS': return { bg: '#f0fdf4', color: '#15803d' };
    default: return { bg: '#f3f4f6', color: '#374151' };
  }
}

/** Convertit du HTML en paragraphes docx (parsing simplifié) */
function htmlToDocx(html: string): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  // Parser le HTML en extrayant les blocs principaux
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return paragraphs;

  function processNode(node: Node): void {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        paragraphs.push(new Paragraph({ children: [new TextRun({ text })], spacing: { after: 120 } }));
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'h1') {
      paragraphs.push(new Paragraph({
        text: el.textContent ?? '',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 480, after: 200 },
      }));
    } else if (tag === 'h2') {
      paragraphs.push(new Paragraph({
        text: el.textContent ?? '',
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 360, after: 160 },
      }));
    } else if (tag === 'h3') {
      paragraphs.push(new Paragraph({
        text: el.textContent ?? '',
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 240, after: 120 },
      }));
    } else if (tag === 'p' || tag === 'blockquote') {
      const runs: TextRun[] = [];
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          runs.push(new TextRun({ text: child.textContent ?? '' }));
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const childEl = child as Element;
          const childTag = childEl.tagName.toLowerCase();
          if (childTag === 'strong' || childTag === 'b') {
            runs.push(new TextRun({ text: childEl.textContent ?? '', bold: true }));
          } else if (childTag === 'em' || childTag === 'i') {
            runs.push(new TextRun({ text: childEl.textContent ?? '', italics: true }));
          } else {
            runs.push(new TextRun({ text: childEl.textContent ?? '' }));
          }
        }
      }
      if (runs.length > 0) {
        paragraphs.push(new Paragraph({
          children: tag === 'blockquote'
            ? [new TextRun({ text: '« ' }), ...runs, new TextRun({ text: ' »', italics: true })]
            : runs,
          spacing: { after: 120 },
          indent: tag === 'blockquote' ? { left: 720 } : undefined,
        }));
      }
    } else if (tag === 'table') {
      // Simplifier les tableaux en texte pour le docx
      const rows = el.querySelectorAll('tr');
      for (const row of Array.from(rows)) {
        const cells = Array.from(row.querySelectorAll('th, td'));
        const text = cells.map(c => c.textContent?.trim()).join(' | ');
        paragraphs.push(new Paragraph({
          children: [new TextRun({ text, font: { name: 'Courier New' }, size: 20 })],
          spacing: { after: 60 },
        }));
      }
    } else {
      // Récursion pour les autres éléments (div, etc.)
      for (const child of Array.from(el.childNodes)) {
        processNode(child);
      }
    }
  }

  for (const child of Array.from(root.childNodes)) {
    processNode(child);
  }

  return paragraphs;
}

/** Formate les modules pour l'affichage */
function formatModules(modules: string): string {
  const map: Record<string, string> = {
    tn: 'Thème natal',
    rs: 'Révolution Solaire',
    prog: 'Progressions',
    tout: 'Analyse complète',
    transits: 'Transits',
  };
  return modules.split(',').map(m => map[m.trim()] ?? m).join(' + ');
}

/** Couleur du badge selon les modules */
function badgeColors(modules: string): { bg: string; color: string; border: string } {
  const m = modules.trim();
  if (m === 'transits') return { bg: '#ecfeff', color: '#0891b2', border: '#a5f3fc' };
  if (m === 'tout')     return { bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
  if (m.includes('prog') && m.includes('rs')) return { bg: '#fce7f3', color: '#be185d', border: '#fbcfe8' };
  if (m.includes('rs'))   return { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' };
  if (m.includes('prog')) return { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' };
  // Thème natal (défaut)
  return { bg: '#f5f3ff', color: '#7c3aed', border: '#e9d5ff' };
}

const MODULE_OPTIONS = [
  { value: 'tn', label: 'Thème natal', icon: 'bi-stars', description: 'Analyse complète du thème natal (5 temps)' },
  { value: 'rs', label: 'Révolution Solaire', icon: 'bi-sun', description: 'Analyse de la RS au regard du natal' },
  { value: 'prog', label: 'Progressions', icon: 'bi-clock-history', description: 'Analyse des progressions secondaires' },
  { value: 'tout', label: 'Analyse complète', icon: 'bi-diagram-3', description: 'TN + RS + Progressions' },
  { value: 'transits', label: 'Transits', icon: 'bi-arrow-left-right', description: 'Analyse des transits en cours' },
];

export function AnalyseIAPage() {
  const { id, chartId } = useParams<{ id: string; chartId: string }>();
  const { client, selectedChartId } = useClient();
  const location = useLocation();

  const activeChartId = chartId ? parseInt(chartId, 10) : selectedChartId;

  const [selectedModules, setSelectedModules] = useState<string[]>(['tn']);
  const [selectedRsYear, setSelectedRsYear] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [savedAnalyses, setSavedAnalyses] = useState<SavedAnalyse[]>([]);
  const [activeAnalyseId, setActiveAnalyseId] = useState<number | null>(null);
  const [sharingAnalyseId, setSharingAnalyseId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(true);

  // RS disponibles pour ce client
  const rsYears = useMemo(() => {
    if (!client?.charts) return [];
    return client.charts
      .filter((c) => c.type === 'REVOLUTION_SOLAIRE' && c.anneeRs)
      .map((c) => c.anneeRs as number)
      .sort((a, b) => b - a);
  }, [client]);

  const contentRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Charger toutes les analyses du client (toutes charts confondues)
  const loadAnalyses = useCallback(() => {
    if (!id) return;

    setLoadingExisting(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    fetch(`${API_BASE}/clients/${id}/analyses-ia`, { headers })
      .then((r) => r.json())
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setSavedAnalyses(res.data as SavedAnalyse[]);
        } else {
          setSavedAnalyses([]);
        }
      })
      .catch(() => setSavedAnalyses([]))
      .finally(() => setLoadingExisting(false));
  }, [id]);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  // Reset quand on clique sur le bouton "Analyse IA" depuis le layout
  const resetKey = (location.state as { reset?: number } | null)?.reset;
  useEffect(() => {
    if (resetKey) {
      setGeneratedText('');
      setActiveAnalyseId(null);
      setError('');
      setSelectedModules(['tn']);
      setSelectedRsYear(null);
    }
  }, [resetKey]);

  // Auto-scroll pendant la génération
  useEffect(() => {
    if (isGenerating && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [generatedText, isGenerating]);

  const handleGenerate = useCallback(async () => {
    if (!id || !activeChartId) return;

    setIsGenerating(true);
    setGeneratedText('');
    setError('');
    setActiveAnalyseId(null);

    const token = localStorage.getItem('token');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const isTransit = selectedModules.includes('transits');
      const endpoint = isTransit ? 'analyse-transit-ia' : 'analyse-ia';

      // Pour les transits, trouver le chartId du chart TRANSITS
      let generateChartId = activeChartId;
      if (isTransit && client?.charts) {
        const transitChart = client.charts
          .filter((c) => c.type === 'TRANSITS')
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (transitChart) {
          generateChartId = transitChart.id;
        } else {
          setError('Aucun chart de transits trouvé. Importez d\'abord des transits.');
          setIsGenerating(false);
          return;
        }
      }

      const response = await fetch(
        `${API_BASE}/clients/${id}/charts/${generateChartId}/${endpoint}/generate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            modules: selectedModules,
            ...(selectedRsYear && { anneeRs: selectedRsYear }),
          }),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          const err = await response.json().catch(() => ({ error: 'Erreur serveur' }));
          throw new Error(err.error ?? `Erreur ${response.status}`);
        }
        throw new Error(`Erreur serveur (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('Pas de stream disponible');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'text') {
              setGeneratedText((prev) => prev + data.content);
            } else if (data.type === 'done') {
              setActiveAnalyseId(data.analyseId);
              loadAnalyses();
            } else if (data.type === 'error') {
              setError(data.content);
            }
          } catch { /* ligne SSE invalide */ }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message);
      }
    } finally {
      setIsGenerating(false);
      abortRef.current = null;
    }
  }, [id, activeChartId, selectedModules, loadAnalyses, client]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleDelete = useCallback(async (analyse: SavedAnalyse) => {
    if (!id) return;
    if (!confirm('Supprimer cette analyse ?')) return;

    const token = localStorage.getItem('token');
    const isTransit = analyse.modules === 'transits';
    const endpoint = isTransit ? 'analyse-transit-ia' : 'analyse-ia';
    // L'analyse est rattachée à un chart précis (potentiellement différent du chart actif).
    const cId = analyse.chart?.id ?? analyse.chartId ?? activeChartId;
    if (!cId) return;

    try {
      const res = await fetch(
        `${API_BASE}/clients/${id}/charts/${cId}/${endpoint}/${analyse.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        setSavedAnalyses((prev) => prev.filter((a) => a.id !== analyse.id));
      }
    } catch { /* silencieux */ }
  }, [id, activeChartId]);

  const handleExportWord = useCallback(async () => {
    if (!generatedText || !client) return;

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: `Analyse astrologique — ${client.prenom} ${client.nom}`,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 300 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Générée le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`,
                italics: true,
                color: '666666',
              }),
            ],
            spacing: { after: 400 },
          }),
          ...htmlToDocx(generatedText),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    const filename = `Analyse_${client.prenom}_${client.nom}_${new Date().toISOString().slice(0, 10)}.docx`;
    saveAs(blob, filename);
  }, [generatedText, client]);

  const handleExportPdf = useCallback(async () => {
    if (!generatedText || !client) return;
    try {
      const dateStr = new Date().toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
      const moduleLabel = formatModules(
        Array.isArray(selectedModules) ? selectedModules.join('+') : 'tn',
      );
      await downloadAnalysePdf({
        title: 'Analyse astrologique',
        subtitle: moduleLabel,
        clientName: `${client.prenom} ${client.nom}`,
        date: dateStr,
        htmlContent: generatedText,
        filename: `Analyse_${client.prenom}_${client.nom}_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Échec de l'export PDF : ${msg}`);
    }
  }, [generatedText, client, selectedModules]);

  if (!client) return null;

  if (!activeChartId) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
        Sélectionnez un thème natal dans la sidebar pour générer une analyse.
      </div>
    );
  }

  return (
    <div>
      <h2 style={{
        margin: '0 0 1.5rem', fontSize: '1.25rem', fontWeight: 700,
        color: colors.text, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <i className="bi bi-robot" style={{ color: '#7c3aed' }} />
        Générateur d'analyse IA
      </h2>

      {/* Sélection des modules */}
      {!isGenerating && !generatedText && (
        <div style={{
          background: '#fff', borderRadius: 10, padding: '1.5rem',
          border: `1px solid ${colors.border}`, marginBottom: '1.5rem',
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: colors.text }}>
            Modules à inclure
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {MODULE_OPTIONS.map((mod) => {
              const isSelected = mod.value === 'tout'
                ? selectedModules.includes('tout')
                : selectedModules.includes(mod.value);

              return (
                <button
                  key={mod.value}
                  onClick={() => {
                    if (mod.value === 'tout' || mod.value === 'transits') {
                      // Exclusifs
                      setSelectedModules([mod.value]);
                    } else {
                      setSelectedModules((prev) => {
                        // Retirer tout/transits
                        const filtered = prev.filter((m) => m !== 'tout' && m !== 'transits');

                        if (filtered.includes(mod.value)) {
                          // Décocher → retirer
                          const next = filtered.filter((m) => m !== mod.value);
                          return next.length > 0 ? next : [mod.value]; // au moins 1
                        }
                        // Cocher → combinaisons autorisées : tn+rs, tn+prog, tn+rs+prog
                        // Si on clique rs ou prog seul (sans tn déjà coché) → mode seul
                        if ((mod.value === 'rs' || mod.value === 'prog') && !filtered.includes('tn')) {
                          return [mod.value];
                        }
                        // Si on clique tn et qu'on avait rs ou prog seul → combiner
                        if (mod.value === 'tn') {
                          return ['tn', ...filtered];
                        }
                        return [...filtered, mod.value];
                      });
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                    border: isSelected ? '2px solid #7c3aed' : `2px solid ${colors.border}`,
                    background: isSelected ? '#f5f3ff' : '#fff',
                    textAlign: 'left', fontFamily: 'inherit', transition: 'all .15s',
                  }}
                >
                  <i className={mod.icon} style={{
                    fontSize: '1.2rem',
                    color: isSelected ? '#7c3aed' : colors.textMuted,
                  }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: isSelected ? '#7c3aed' : colors.text }}>
                      {mod.label}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                      {mod.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Sélecteur d'année RS */}
          {(selectedModules.includes('rs') || selectedModules.includes('tout')) && rsYears.length > 0 && (
            <div style={{
              marginTop: '1rem', padding: '12px 16px', borderRadius: 8,
              background: '#fff7ed', border: '1px solid #fed7aa',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <i className="bi bi-calendar-event" style={{ color: '#c2410c', fontSize: '1.1rem' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#c2410c', marginBottom: 4 }}>
                  Année de la Révolution Solaire
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {rsYears.map((year) => (
                    <button
                      key={year}
                      onClick={() => setSelectedRsYear(year === selectedRsYear ? null : year)}
                      style={{
                        padding: '4px 12px', borderRadius: 6, fontSize: '0.85rem', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                        border: (selectedRsYear ?? rsYears[0]) === year
                          ? '2px solid #c2410c' : '1px solid #fed7aa',
                        background: (selectedRsYear ?? rsYears[0]) === year
                          ? '#c2410c' : '#fff',
                        color: (selectedRsYear ?? rsYears[0]) === year
                          ? '#fff' : '#c2410c',
                      }}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={selectedModules.length === 0}
            style={{
              marginTop: '1.25rem', padding: '12px 28px', borderRadius: 8,
              border: 'none', background: '#7c3aed', color: '#fff',
              fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8,
              opacity: selectedModules.length === 0 ? 0.5 : 1,
            }}
          >
            <i className="bi bi-magic" />
            Générer l'analyse
          </button>

          {loadingExisting && (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: colors.textMuted }}>
              Vérification d'une analyse existante...
            </p>
          )}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '10px 14px', fontSize: 14, color: '#991b1b',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem',
        }}>
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      )}

      {/* Contenu généré */}
      {(isGenerating || generatedText) && (
        <div style={{
          background: '#fff', borderRadius: 10,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}>
          {/* Barre d'outils */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
            background: '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isGenerating && (
                <>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#7c3aed',
                    animation: 'pulse 1.5s infinite',
                  }} />
                  <span style={{ fontSize: '0.85rem', color: '#7c3aed', fontWeight: 500 }}>
                    Génération en cours...
                  </span>
                </>
              )}
              {!isGenerating && generatedText && (
                <>
                  <i className="bi bi-check-circle-fill" style={{ color: '#16a34a' }} />
                  <span style={{ fontSize: '0.85rem', color: '#16a34a', fontWeight: 500 }}>
                    Analyse terminée
                  </span>
                  {activeAnalyseId && (
                    <span style={{ fontSize: '0.8rem', color: colors.textMuted, marginLeft: 8 }}>
                      (sauvegardée automatiquement)
                    </span>
                  )}
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {isGenerating && (
                <button
                  onClick={handleStop}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.border}`,
                    background: '#fff', color: colors.textMid, fontSize: '0.8rem',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  <i className="bi bi-stop-circle" style={{ marginRight: 4 }} />
                  Arrêter
                </button>
              )}
              {!isGenerating && generatedText && (
                <>
                  {activeAnalyseId && (
                    <button
                      onClick={() => setSharingAnalyseId(activeAnalyseId)}
                      style={{
                        padding: '6px 14px', borderRadius: 6, border: '1px solid #c9a227',
                        background: '#fff', color: '#8a6e16', fontSize: '0.8rem',
                        cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                        alignItems: 'center', gap: 4,
                      }}
                    >
                      <i className="bi bi-share" />
                      Partager
                    </button>
                  )}
                  <button
                    onClick={handleExportPdf}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #dc2626',
                      background: '#fff', color: '#dc2626', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                      alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="bi bi-file-earmark-pdf" />
                    Export PDF
                  </button>
                  <button
                    onClick={handleExportWord}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #2563eb',
                      background: '#fff', color: '#2563eb', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                      alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="bi bi-file-earmark-word" />
                    Export Word
                  </button>
                  <button
                    onClick={() => {
                      setGeneratedText('');
                      setActiveAnalyseId(null);
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: `1px solid #7c3aed`,
                      background: '#fff', color: '#7c3aed', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                      alignItems: 'center', gap: 4,
                    }}
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                    Regénérer
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Texte de l'analyse */}
          <div
            ref={contentRef}
            className="rich-content"
            style={{
              padding: '2rem', maxHeight: '70vh', overflowY: 'auto',
              lineHeight: 1.8, fontSize: '0.95rem',
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(generatedText),
            }}
          />
        </div>
      )}

      {/* Historique des analyses */}
      {!isGenerating && savedAnalyses.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 10, padding: '1.5rem',
          border: `1px solid ${colors.border}`,
        }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: colors.text }}>
            <i className="bi bi-clock-history" style={{ marginRight: 6, color: '#7c3aed' }} />
            Analyses précédentes ({savedAnalyses.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {savedAnalyses.map((analyse) => (
              <div
                key={analyse.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderRadius: 8,
                  border: `1px solid ${colors.border}`, background: '#fafafa',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
                      background: chartBadgeColors(analyse.chart?.type).bg,
                      color: chartBadgeColors(analyse.chart?.type).color,
                    }}>
                      <i className="bi bi-diagram-2" style={{ marginRight: 4 }} />
                      {chartLabel(analyse.chart)}
                    </span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
                      background: badgeColors(analyse.modules).bg,
                      color: badgeColors(analyse.modules).color,
                      border: `1px solid ${badgeColors(analyse.modules).border}`,
                    }}>
                      {formatModules(analyse.modules)}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                      {new Date(analyse.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <p style={{
                    margin: 0, fontSize: '0.8rem', color: colors.textMid,
                    lineHeight: 1.5, maxHeight: 40, overflow: 'hidden',
                  }}>
                    {analyse.contenu.replace(/<[^>]*>/g, '').slice(0, 150)}...
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                  <button
                    onClick={() => {
                      setGeneratedText(analyse.contenu);
                      setActiveAnalyseId(analyse.id);
                    }}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: '#fff', color: colors.text, fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-eye" style={{ marginRight: 4 }} />
                    Voir
                  </button>
                  <button
                    onClick={() => setSharingAnalyseId(analyse.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: '1px solid #c9a227',
                      background: '#fff', color: '#8a6e16', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-share" style={{ marginRight: 4 }} />
                    Partager
                  </button>
                  <button
                    onClick={() => handleDelete(analyse)}
                    style={{
                      padding: '6px 10px', borderRadius: 6, border: '1px solid #fca5a5',
                      background: '#fff', color: '#dc2626', fontSize: '0.8rem',
                      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Styles CSS pour l'analyse et le point clignotant */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .rich-content h1 { font-size: 1.5rem; font-weight: 700; margin: 2rem 0 1rem; color: #1e1b4b; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5rem; }
        .rich-content h2 { font-size: 1.25rem; font-weight: 600; margin: 1.75rem 0 0.75rem; color: #312e81; }
        .rich-content h3 { font-size: 1.1rem; font-weight: 600; margin: 1.25rem 0 0.5rem; color: #4338ca; }
        .rich-content p { margin: 0.5rem 0; }
        .rich-content blockquote { border-left: 3px solid #7c3aed; padding: 0.75rem 1rem; margin: 1rem 0; background: #f5f3ff; font-style: italic; color: #4c1d95; border-radius: 0 6px 6px 0; }
        .rich-content table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
        .rich-content th { background: #f3f4f6; font-weight: 600; text-align: left; padding: 8px 12px; border: 1px solid #e5e7eb; }
        .rich-content td { padding: 8px 12px; border: 1px solid #e5e7eb; }
        .rich-content tr:nth-child(even) { background: #f9fafb; }
        .rich-content strong { color: #1e1b4b; }
      `}</style>

      {sharingAnalyseId !== null && (
        <ShareAnalyseModal
          analyseId={sharingAnalyseId}
          onClose={() => setSharingAnalyseId(null)}
        />
      )}
    </div>
  );
}
